/** Even a small charge stays golden, distinct from the empty track. */
const EMPTY_LIGHTNESS = 0.9;
const EMPTY_CHROMA = 0.14;
/** OKLCH at a full bar: Duolingo's XP-bar yellow (#FFC800) while charging, orange while spending. */
const CHARGING_FULL = { chroma: 0.1752, hue: 88.49, lightness: 0.8575 };
const SPENDING_FULL = { chroma: 0.18, hue: 58, lightness: 0.8 };

/**
 * A progress meter's fill (`ProgressMeter`): pale when nearly empty, deepening as it fills. Charging
 * is yellow, ending at Duolingo's XP-bar yellow, and spending (the review recharge bar's charge
 * being used up) is orange. Chroma past what sRGB can show at
 * that lightness is reduced (orange runs out of gamut much sooner than yellow near white), never
 * the lightness.
 */
export function getProgressMeterFillColor({
  fraction,
  isSpending,
}: {
  fraction: number;
  isSpending: boolean;
}): string {
  const clampedFraction = Math.max(0, Math.min(1, fraction));
  const full = isSpending ? SPENDING_FULL : CHARGING_FULL;
  const lightness = EMPTY_LIGHTNESS + (full.lightness - EMPTY_LIGHTNESS) * clampedFraction;
  const targetChroma = EMPTY_CHROMA + (full.chroma - EMPTY_CHROMA) * clampedFraction;
  const { hue } = full;
  const chroma = isInSrgbGamut(oklchToLinearSrgb(lightness, targetChroma, hue))
    ? targetChroma
    : findMaxInGamutChroma(lightness, targetChroma, hue);
  return linearSrgbToHex(oklchToLinearSrgb(lightness, chroma, hue));
}

function findMaxInGamutChroma(lightness: number, maxChroma: number, hue: number): number {
  let low = 0;
  let high = maxChroma;
  for (let step = 0; step < 16; step++) {
    const middle = (low + high) / 2;
    if (isInSrgbGamut(oklchToLinearSrgb(lightness, middle, hue))) {
      low = middle;
    } else {
      high = middle;
    }
  }
  return low;
}

function oklchToLinearSrgb(
  lightness: number,
  chroma: number,
  hue: number,
): [number, number, number] {
  const hueRadians = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(hueRadians);
  const b = chroma * Math.sin(hueRadians);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function isInSrgbGamut(channels: [number, number, number]): boolean {
  return channels.every((channel) => channel >= -1e-4 && channel <= 1 + 1e-4);
}

function linearSrgbToHex(channels: [number, number, number]): string {
  return `#${channels
    .map((channel) => {
      const linear = Math.max(0, Math.min(1, channel));
      const encoded = linear <= 0.0031308 ? 12.92 * linear : 1.055 * linear ** (1 / 2.4) - 0.055;
      return Math.round(encoded * 255)
        .toString(16)
        .padStart(2, "0");
    })
    .join("")}`;
}
